import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Card, CardActionArea, CardContent, CardMedia, Typography } from "@mui/material";
import { renderRichText, storyblokEditable, StoryblokRichTextNode, useStoryblokApi } from "@storyblok/react";
import Grid from "@mui/material/Grid2";
import { formatDateByLocale } from "utils/formateDate";
import CustomButton from "components/Button";

interface IArticleProps {
  blok: {
    main_title: string;
    main_title_color: string;
    main_title_backdrop: string;
    main_image: any;
    body: StoryblokRichTextNode<string | TrustedHTML>;
    creation_date: string;
    creation_date_color: string;
    seo: { title: string; description: string };
  };
}

const Article = ({ blok }: IArticleProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const html = renderRichText(blok.body) ?? "";
  const storyBlokApi = useStoryblokApi();
  const [allArticles, setAllArticles] = useState<any[]>([]);
  const lastFiveArticles = allArticles.slice(0, 5);

  const getAllStoryblokStories = async () => {
    const res = await storyBlokApi.get("cdn/stories", {
      version: "draft",
      language: currentLanguage,
      per_page: 100,
    });

    if (!res.data.stories || res.data.stories.length === 0) {
      return;
    }

    const filtredArticles = res.data.stories.filter((story: any) => {
      return story.name !== "Home";
    });

    setAllArticles(filtredArticles);
  };

  useEffect(() => {
    void getAllStoryblokStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  return (
    <>
      <Grid container spacing={3} {...storyblokEditable(blok)}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <CustomButton
            width={"max-content"}
            height={30}
            textButton={t("tohomepage")}
            customBgColor="#F4701B"
            customHoverColor="#f0670b"
            customBorderRadius="16px"
            customFontSize="14px"
            customMargin="0px 0px 11px 0px"
            customPadding="8px 24px"
            customTextTransform="none"
            onClick={() => navigate(`/blog/${currentLanguage}`)}
          />
          <Box sx={{ position: "relative", marginBottom: "24px" }}>
            <Box
              component="img"
              src={blok.main_image.filename}
              alt={blok.main_image.alt}
              sx={{
                display: "block",
                width: "100%",
                height: { xs: 340, sm: 300, md: 380 },
                objectFit: "cover",
                borderRadius: "16px",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                padding: { xs: "16px", md: "24px" },
                backgroundColor: `${blok.main_title_backdrop}`,
                backdropFilter: "blur(2px)",
                borderBottomLeftRadius: "16px",
                borderBottomRightRadius: "16px",
              }}
            >
              {blok.creation_date && (
                <Typography
                  component="span"
                  sx={{
                    display: "inline-block",
                    marginBottom: "6px",
                    fontSize: "12px",
                    lineHeight: 1.24,
                    color: `${blok.creation_date_color || "#6f6a78"}`,
                  }}
                >
                  {formatDateByLocale(blok.creation_date)}
                </Typography>
              )}

              <Typography
                component="h1"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: 20, md: 24 },
                  lineHeight: 1.26,
                  color: `${blok.main_title_color || "#2E263D"}`,
                }}
              >
                {blok.main_title}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              "& h2, & h3, & h4": {
                marginBottom: "8px",
                fontFamily: "var(--cygreMedium), sans-serif",
                fontSize: "24px",
                fontWeight: 600,
                lineHeight: 1.6,
                color: "#2E263D",
              },

              "& h3": {
                fontSize: "20px",
              },

              "& h4": {
                fontSize: "18px",
                fontWeight: 500,
              },

              "& p": {
                marginBottom: "16px",
                fontFamily: "var(--cygreMedium), sans-serif",
                fontSize: "16px",
                lineHeight: 1.6,
                color: "#2E263D",
              },

              "& img": {
                display: "block",
                width: "100%",
                maxWidth: 900,
                height: "auto",
                marginBottom: "16px",
                borderRadius: "16px",
              },

              "& strong": {
                fontWeight: 600,
              },

              "& a": {
                cursor: "pointer",
                color: "#F4701B",
                textDecoration: "none",
                transition: "color 0.3s",
                "&:hover": {
                  color: "#F0670B",
                },
              },

              "& ul, & ol": {
                paddingLeft: "20px",
                marginBottom: "16px",
              },

              "& ul": {
                listStyle: "none",
              },

              "& li": {
                marginBottom: "8px",
                fontFamily: "var(--cygreMedium), sans-serif",
                fontSize: "16px",
                lineHeight: 1.6,
                color: "#2E263D",
              },

              "& ul li": {
                position: "relative",
                paddingLeft: "24px",

                "&::before": {
                  content: "''",
                  position: "absolute",
                  left: 0,
                  top: 0,
                  transform: "translateY(100%)",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#F4701B",
                  borderRadius: "50%",
                },
              },

              "& ol": {
                listStyleType: "decimal",
                paddingLeft: "44px",

                "& li::marker": {
                  fontWeight: 600,
                  color: "#2E263D",
                  fontSize: "16px",
                },
              },

              "& u": {
                textDecoration: "underline",
              },

              "& em, & i": {
                fontStyle: "italic",
              },
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </Grid>
        <Grid
          size={{ xs: 0, lg: 4 }}
          sx={{ display: { xs: "none", lg: "flex" }, flexDirection: "column", gap: "16px" }}
        >
          <Typography
            component="h2"
            sx={{
              textAlign: "center",
              color: "#2E263D",
              fontWeight: 500,
              fontSize: "20px",
              lineHeight: 1.24,
            }}
          >
            {t("latestPosts")}
          </Typography>

          {lastFiveArticles.map((article) => {
            const articleTitle = article?.content?.main_title;
            const articlePreviewText = article?.content?.preview_text;
            const articlePreviewImage = article?.content?.preview_image?.filename;
            const articlePreviewImageAlt = article?.content?.preview_image?.alt || "";
            const fullSlug = article?.full_slug;
            const articleKey = article?.uuid;

            return (
              <Card
                key={articleKey}
                sx={{ height: "auto", borderRadius: "24px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)" }}
              >
                <CardActionArea
                  onClick={() =>
                    navigate(currentLanguage === "en" ? `/blog/${fullSlug}` : `/blog/${currentLanguage}/${fullSlug}`)
                  }
                >
                  <CardMedia component="img" height="180" image={articlePreviewImage} alt={articlePreviewImageAlt} />
                  <CardContent>
                    <Typography
                      gutterBottom
                      component="h3"
                      sx={{ color: "#2E263D", fontSize: "18px", fontWeight: 600, lineHeight: "24px" }}
                    >
                      {articleTitle}
                    </Typography>
                    <Typography
                      component="p"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        color: "#2E263D",
                        fontSize: "16px",
                        lineHeight: "24px",
                      }}
                    >
                      {articlePreviewText}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Grid>
      </Grid>
    </>
  );
};

export default Article;
